// fetch
import { post, postWithToken, getWithToken } from './fetch.js';

// urls
import urls from './_services.json';

// util
import { getUrl, isXingliu } from './util.js';

import { getCookieWebid } from '@shakkerai/util-cookie';

/**
 * 请求模型库列表
 *
 * @param {*} params
 * @returns
 */
export async function requestModelList(params) {
  const url = getUrl(urls.modelSearch);
  return await post(url, {
    ...params,
  });
}

/**
 * 请求模型分类标签
 * @param {*} params
 * @returns
 */
export async function requestModelTags(params) {
  const url = `${getUrl(urls.modelTagSearch)}?timestamp=${Date.now()}`;
  return await post(url, {
    ...params,
  });
}

const cachedModelDetails = {};

/**
 * 请求模型详情
 * @param {*} uuid
 * @returns
 */
export async function requestModelDetail(uuid, useCache = true) {
  if (useCache && cachedModelDetails[uuid]) {
    return cachedModelDetails[uuid];
  }

  const url = getUrl(urls.requestModelDetail + '/' + uuid);
  const result = await post(url, {});

  if (result?.data) {
    cachedModelDetails[uuid] = result;
  }

  return result;
}

/**
 * 请求推荐的Lora模型
 * @param {*} uuid
 * @returns
 */
export async function requestRecommendLora(uuid) {
  const url = getUrl(urls.requestRecommendLora + '/' + uuid);
  return await post(url, {});
}

const cachedPrompts = {};

export function updatePromptCache(prompts, data) {
  cachedPrompts[prompts] = data;
}

/**
 * 请求prompt联想扩写
 * @param { string[] } prompts
 * @returns
 */
export async function requestRecommendPrompt(prompts = '') {
  if (cachedPrompts[prompts]) {
    return cachedPrompts[prompts];
  }

  const cid = getCookieWebid();
  const url = getUrl(isXingliu ? urls.requestRecommendPrompt : urls.requestRecommendPromptShakker);

  const result = await postWithToken(url, { prompts, cid }).then((res) => res.data);

  if (result) {
    cachedPrompts[prompts] = result;
  }

  return cachedPrompts[prompts] || '';
}

export async function searchStyleList(params = {}) {
  const url = getUrl(urls.searchStyleList);
  return await postWithToken(url, params);
}

export async function searchModelList(params = {}) {
  const url = getUrl(urls.searchModelList);
  return await postWithToken(url, params);
}

export async function getStyleDetail(params) {
  const url = getUrl(urls.getStyleDetail);
  return await postWithToken(url, params);
}
// { styleImageUuid: "" }
export async function getImageParams(params) {
  const url = getUrl(urls.getImageParams);
  const res = await postWithToken(url, params);

  if (res?.data?.baseModel) {
    if (res.data.baseModel.baseType === 'SD XL 0.9') {
      res.data.baseModel.baseType = 'SD XL';
    }
  }

  if (Array.isArray(res?.data?.loraModes)) {
    res.data.loraModes = res.data.loraModes.map((item) => {
      if (item.baseType === 'SD XL 0.9') {
        item.baseType = 'SD XL';
      }

      return item;
    });
  }

  return res;
}

export async function getTagList(params) {
  const url = getUrl(urls.getTagList);
  return await postWithToken(url, params);
}

/**
 * gallery列表搜索
 * @param { number } limit
 * @param { string } tagCode
 * @param { string } query
 * @returns
 */
export async function searchGalleryList(params) {
  const url = getUrl(urls.searchGallery);
  return await postWithToken(url, { ...params });
}

/**
 * 请求model使用历史
 * @param { number } pageNo
 * @param { number } pageSize
 * @returns
 */
export async function getModelHistory(params = {}) {
  const url = getUrl(urls.getModelHistory);
  return await postWithToken(url, params);
}

/**
 * style点赞收藏
 * @param { string } uuid
 * @param { string } extraUuid
 * @returns
 * https://app.apifox.com/project/3835059
 */
export async function addStyleToFavorite(params) {
  const url = getUrl(urls.addStyleToFavorite);
  return await postWithToken(url, {
    type: 3,
    ...params,
  });
}

export async function getGenerateInfo(params) {
  const url = getUrl(urls.getGenerateInfo);
  return await postWithToken(url, params);
}

export async function getFavoriteModel(params) {
  const url = getUrl(urls.getFavoriteModel);
  return await postWithToken(url, params);
}

/**
 * 请求用户信息
 * @param {*} uuid
 * @returns
 */
export async function requestUserInfo(uuid) {
  const url = getUrl(urls.requestUserInfo + '/' + uuid);
  return await post(url, {});
}

/**
 * 请求生成历史
 * @returns
 */
export async function requestGenHistory(params) {
  const url = getUrl(urls.requestGenHistoryList);
  return await postWithToken(url, {
    ...params,
  });
}

/**
 * 请求生成历史的详细参数
 * @param {*} uuid
 * @returns
 */
export async function requestGenHistoryParams(params) {
  const url = getUrl(urls.requestGenHistoryParams);
  return await postWithToken(url, {
    ...params,
  });
}

/**
 * 删除生成历史的某一项
 * @param {*} uuid
 * @returns
 */
export async function deleteGenHistoryItem(params) {
  const url = getUrl(urls.deleteGenHistoryItem);
  return await postWithToken(url, {
    ...params,
  });
}

/**
 * 收藏生成历史的一项或多项，https://app.apifox.com/project/2950219/apis/api-114157233
 * @param {*} params
 * @returns
 */
export async function collectGenHistoryItem(params) {
  const url = getUrl(urls.collectImage);
  return await postWithToken(url, params);
}

/**
 * 取消收藏，https://app.apifox.com/project/2950219/apis/api-114157233
 *
 * @param {*} params
 * @returns
 */
export async function cancelCollectGenHistoryItem(params) {
  const url = getUrl(urls.cancelCollectImage);
  return await postWithToken(url, params);
}

/**
 * 创建专辑
 *
 * @param {*} params
 * @returns
 */
export async function postCreateAblum(params) {
  const url = getUrl(urls.createAblum);
  return await postWithToken(url, params);
}

/**
 * 编辑专辑信息
 * @param {*} params
 * @returns
 */
export async function postEditAblum(params) {
  const url = getUrl(urls.editAblum);
  return await postWithToken(url, params);
}

/**
 * 删除专辑
 * @param {*} params
 * @returns
 */
export async function postDeleteAblum(params) {
  const url = getUrl(urls.deleteAblum);
  return await postWithToken(url, params);
}

/**
 * 获取专辑列表
 * @param {*} params
 * @returns
 */
export async function postAblumList(params) {
  const url = getUrl(urls.ablumList);
  return await postWithToken(url, params);
}

/**
 * 获取专辑详情
 * @param {*} params
 * @returns
 */
export async function postAblumDetail(params) {
  const url = getUrl(urls.ablumDetail);
  return await postWithToken(url, params);
}

/**
 * 获取图片分享开关状态
 * @param {*} params
 * @returns
 */
export async function getSharePicState() {
  const url = getUrl(urls.getSharePicState);
  return await getWithToken(url);
}

/**
 * 设置图片分享开关状态
 * @param {*} params
 * @returns
 */
export async function setSharePicState(params) {
  const url = getUrl(urls.setSharePicState);
  return await getWithToken(url, params);
}

/**
 * 获取生图历史首页数据
 * @param {*} params
 * @returns
 */
export async function postHistoryHome(params) {
  const url = getUrl(urls.historyHomeList);
  return await postWithToken(url, params);
}

/**
 * 获取空间使用情况
 * @param {*} params
 * @returns
 */
export async function requestUserStorage() {
  const url = getUrl(urls.requestUserStorage);
  return await getWithToken(url);
}

/**
 * 下载生成历史的某一项
 * @param {*} uuid
 * @returns
 */
export async function downloadGenHistoryItem(uuid) {
  const url = getUrl(urls.downloadGenHistoryItem + '/' + uuid);
  return await postWithToken(url, {});
}

/**
 * 获取某一历史任务
 * @param {*} params
 * @returns
 */
export async function requestTaskDetail(params) {
  const url = getUrl(urls.imageDetail);
  return await postWithToken(url, params);
}

export async function requestTaskProjectData(pnginfoId) {
  return await postWithToken(getUrl(urls.getTaskProjectData), { pnginfoId });
}

export async function requestImageGenerationInfo(id) {
  const url = getUrl(urls.imageGenerationInfo);
  return await postWithToken(url + id, {});
}

export async function requestImageGroupInfo(groupId) {
  const url = getUrl(urls.imageGroupInfo);
  return await postWithToken(url + groupId, {});
}

/**
 * Style是否可用
 * @param {*} param
 * @returns
 */
export async function requestStyleEnable(param) {
  const url = getUrl(urls.styleEnable);
  return await postWithToken(url, param);
}

const GENERATE_POLLING_INTERVAL = 3000;

export async function requestModelsByImage(sourceImage) {
  const url = getUrl(urls.requestModelsByImage);
  const { data, msg } = await postWithToken(url, { sourceImage });

  if (!data || !Array.isArray(data)) throw new Error(msg);

  return data;
}

export async function requestTaskStatus(taskId, params) {
  return postWithToken(getUrl(urls.requestGenerateStatus + taskId), params).then((res) => res.data);
}

export async function pollingTaskStatus(taskId, options = {}) {
  let continuePolling = true;

  while (continuePolling) {
    const params = {};

    params.flag = 3; // 2024年05月17日16:28:01 Pic和shakker都是3，和井淼确认的

    const result = await postWithToken(getUrl(urls.requestGenerateStatus + taskId), params).then((res) => res.data);
    options.onUpdate && options.onUpdate(result, options.subTaskLocalId);

    if (!result) {
      // 如果没有返回结果，继续轮询
      await new Promise((resolve) => setTimeout(resolve, options.pollingInterval || GENERATE_POLLING_INTERVAL));
      continue;
      // continuePolling = false;
      // throw { message: 'network_error', code: 500 };
    }

    if (result.errCode) {
      continuePolling = false;
      throw { message: result.statusMsg || result.errCode, code: result.errCode };
    }

    if (!result.generateId) {
      continuePolling = false;
      throw { message: '任务已失效', code: 404 };
    }

    if (options.successAssert && options.successAssert(result)) {
      continuePolling = false;
      return result;
    } else if (result.images && result.images.length) {
      continuePolling = false;
      return result;
    } else if (result.videos && result.videos.length) {
      continuePolling = false;
      return result;
    } else if (result.subStatus === 2 && !result.images?.length && !result.videos?.length) {
      continuePolling = false;
      throw { message: '任务异常', code: 500 };
    }

    await new Promise((resolve) => setTimeout(resolve, options.pollingInterval || GENERATE_POLLING_INTERVAL));
  }
}

export async function requestFirstLookParams(params) {
  return postWithToken(getUrl(urls.getFirstLookParams), params);
}
export async function requestStyleCodeParams(params) {
  return postWithToken(getUrl(urls.requestStyleCodeParams), params);
}

export async function requestShakeImage(params, options = {}) {
  if (Array.isArray(params.subTasks)) {
    for (const subTaskIndex in params.subTasks) {
      try {
        const subTask = params.subTasks[subTaskIndex];
        const { params: generateParams, projectData } = subTask.params;
        const subTaskParams = { ...params, ...generateParams, customMade: 1001, subTasks: null };

        subTaskParams.projectData = Object.assign({}, subTaskParams.projectData, projectData);

        requestShakeImage(subTaskParams, { subTaskLocalId: subTask.id, ...options })
          .then((result) => {
            result && options?.onSubTaskComplete(subTask.id, subTaskIndex, result, subTaskParams.projectData);
          })
          .catch((e) => {
            console.log(`[DEBUG] requestShakeImage error`, e);
            options.onError && options.onError({ code: e.code, message: e.message, subTaskLocalId: subTask.id });
          });
      } catch (e) {
        console.log(`[DEBUG] requestShakeImage error`, e);
      }
    }

    return null;
  }

  const isComfyTask = !!params.comfyParams;
  const isWorkflowTask = !!params.workflowParams;

  let url, generateParams;

  if (isComfyTask) {
    generateParams = params.comfyParams;
    url = getUrl(urls.requestShakeComfyImage);
  } else if (isWorkflowTask) {
    generateParams = params.workflowParams;
    url = getUrl(urls.requestShakeImage);
  } else {
    generateParams = params;
    url = getUrl(urls.requestShakeImage);
  }

  // 如果存在shapes,把shapes删掉
  let finalParams;
  if (generateParams?.projectData?.segments?.shapes) {
    finalParams = {
      ...generateParams,
      projectData: {
        ...generateParams.projectData,
        segments: {
          ...generateParams.projectData.segments,
          shapes: undefined,
        },
      },
    };
  } else {
    finalParams = generateParams;
  }

  const { data: taskId, code, msg } = await postWithToken(url, { source: 3, ...finalParams });

  if (!taskId) {
    options.onError && options.onError({ code, message: msg, subTaskLocalId: options.subTaskLocalId });
    return;
  }

  options.onStart && options.onStart(taskId, options.subTaskLocalId);
  return pollingTaskStatus(taskId, options);
}

export async function requestPreviewImage(params, options = {}) {
  const url = getUrl(urls.requestPreviewImage);
  const { image, code, msg } = await postWithToken(url, params);

  if (!image) {
    options.onError && options.onError({ code, message: msg });
    return;
  }

  return image;
}

export async function requestTranslatePrompt(text, params = {}) {
  const url = getUrl(urls.requestTranslatePrompt);
  return await postWithToken(url, {
    reqSourceType: 'pro',
    sourceLanguage: 'zh',
    sourceText: text,
    targetLanguage: 'en',
    ...params,
  });
}

export async function requestCloudData(params) {
  const url = getUrl(urls.requestProCanvasData);
  return await postWithToken(url, {
    ...params,
  });
}

export async function updateCanvasData(params) {
  const url = getUrl(urls.updateProCanvasData);
  return await postWithToken(url, {
    ...params,
  });
}

export async function requestRecommendModels(params) {
  const url = getUrl(urls.recommendModels);
  return await postWithToken(url, {
    ...params,
  });
}

export async function requestRevStyle(imagePath) {
  const url = getUrl(urls.requestRevStyle);
  return await postWithToken(url, {
    messages: [imagePath],
    question:
      'Assess the image and assign it a substyle from the following options:\nAnime (Style):\n  Japanese Anime style, American Comic style (Substyles)\nPhotography (Style):\n  Portrait Photography, Landscape Photography, Surreal abstract photography, Nature Photography, Street Photography (Substyles)\nIllustration (Style):\n  Traditional Painting Media, Digital Painting Media (Substyles)\nMockups (Style):\n  Product Poster, Logo design, Background asset, Game asset (Substyles)\n3D Art (Style):\n  Cartoon style, CGI style (Substyles)\nArchitectural & Interiors (Style):\n  Architectural Design, Interior Design, Landscape Design (Substyles)\nSubsequently, enumerate 5 crucial components present in the image. Highlight particular individuals, apparel, prominent objects, or distinctive features. Last, propse a detailed description including style, main elements, e.t.c, that can be used as prompt to generate this image comprehensively. Restrict each tag at most 3 words. Structure your answer precisely as follows:\n[Style: chosen_style] [Substyle: chosen_substyle] [Tags: tag1, tag2, tag3, tag4, tag5] [Description: description]',
    sampling_params: { max_tokens: 256, use_beam_search: true, n: 5, temperature: 0 },
  });
}

export async function requestRevPromptV2(imagePath) {
  const url = getUrl(urls.requestRevStyle);

  return await postWithToken(url, {
    messages: [imagePath],
    question:
      'Assess the image propose a detailed description including style, main elements, e.t.c, that can be used as prompt to generate this image comprehensively, Structure your answer precisely as follows:\n[Description: description]',
    sampling_params: { max_tokens: 256 },
  }).then((res) => {
    return res?.response?.[0]?.description;
  });
}

export async function requestRevPrompt(params) {
  const url = getUrl(urls.requestRevPrompt);
  return await postWithToken(url, {
    ...params,
  });
}

export async function requestSegmentImage(params) {
  const url = getUrl(urls.imageSegmentation);
  return await postWithToken(
    url,
    {
      ...params,
    },
    null,
    {
      timeout: 120000,
    },
  );
}

export async function requestSmartRemoveImage(params) {
  const url = getUrl(urls.imageSmartRemove);
  return await postWithToken(url, {
    ...params,
  });
}

export async function requestCutoutImage(params) {
  const url = getUrl(urls.imageCutout);
  return await postWithToken(url, {
    ...params,
  });
}

export async function requestPresetLibTags() {
  const url = getUrl(urls.presetLibTags);
  return await postWithToken(url, {});
}

export async function requestPresetConfigs() {
  const url = getUrl(urls.presetConfigs);
  return await postWithToken(url, {});
}

export async function requestPresetConfigDetail(params) {
  const url = getUrl(urls.requestPresetConfigDetail);
  return await postWithToken(url, {
    ...params,
  });
}

export async function requestCommunityModelDetail(uuid) {
  const url = getUrl(urls.getCommunityModelDetail + '/' + uuid);
  return await postWithToken(url, {});
}

const cachedModelVersionDetail = {};

export async function requestModelVersionDetail(uuid, useCache = true) {
  if (useCache && cachedModelVersionDetail[uuid]) {
    return cachedModelVersionDetail[uuid];
  }

  const url = getUrl(urls.getModelDetail + '/' + uuid);
  const result = await postWithToken(url, {});

  if (result?.data) {
    cachedModelVersionDetail[uuid] = result;
  }

  if (result.data && !result.data.imageUrl && result.data.images?.length) {
    result.data.imageUrl = result.data.images[0].imageUrl;
  }

  return result;
}

export async function requetModelVersionsByIdList(versionIds) {
  const url = getUrl(urls.requestModelVersionsByIdList);
  return postWithToken(url, { versionIds });
}

export async function requestModelDetailByUuid(uuid) {
  const url = getUrl(urls.getModelDetailByUuid + '/' + uuid);
  return await postWithToken(url, {});
}

/**
 * model收藏
 * @param { string } modelVersionId
 * @param { string } subscribeType 1
 * @param { string } sourceType TOOL
 * @returns
 */
export async function requestModelCollect(params = {}) {
  const _url = params?.type === 0 ? urls.unsubscribeModelFavorite : urls.subscribeModelFavorite;

  const url = getUrl(_url);
  return await postWithToken(url, {
    ...params,
    subscribeType: 1,
    sourceType: 'TOOL',
    modelVersionId: params?.uuid,
  });
}

export async function getImageInfo(params) {
  const url = getUrl(urls.getImageInfo);
  return await postWithToken(url, {
    ...params,
  });
}

export async function request(url, params) {
  return await postWithToken(getUrl(url), {
    ...params,
  });
}

export async function requestByGet(url, params) {
  return await getWithToken(getUrl(url), {
    ...params,
  });
}

export async function getPromptGuideList(params) {
  const url = getUrl(urls.getPromptGuideList);
  return await getWithToken(url, {
    ...params,
  });
}

// 用户上传到持久化bucket的图片
// model-ai-test -> liblibai-gen-images

/**
 * 获取图片调色盘
 * @param {string} sourceImageId 图片ID
 * @returns {Promise<{colorPalette: string[]}>} 调色盘颜色列表
 */
export async function getImageColorPalette(sourceImageId) {
  const url = getUrl(urls.getImageColorPalette);
  return await postWithToken(url, {
    sourceImageId,
  });
}
